import { Navigate, useLocation } from "react-router-dom";

import { EmptyState } from "@/components/ui/empty-state";
import { useAuthSession } from "@/lib/session-context";
import { isTabAuthorized, type TabRoute } from "@/lib/routes";
import { Lock } from "lucide-react";

interface TabIndexRedirectProps {
  /** The parent's full child-tab list (unfiltered). */
  tabs: ReadonlyArray<TabRoute>;
}

/**
 * Index element for a tabbed detail route (`/customers/:id`, `/deals/:id`, …).
 * Redirects a bare parent URL to the caller's first *authorized, enabled* tab.
 *
 * Why this can't be a static `<Navigate>`: the first tab a user should land on
 * depends on their permission set, which only exists at render time (in the
 * session), not at router-build time. A static redirect to the first
 * non-`disabled` tab points everyone at the same tab (e.g. Contacts) — and if
 * the caller lacks that tab's `requiredPermission`, `<TabGuard>` bounces them
 * back up to this index, which redirects to the same forbidden tab again: an
 * infinite loop. Picking the first *visible* tab here matches what the tab bar
 * (`isTabAuthorized` filter in the parent page) and `<TabGuard>` already assume.
 *
 * When the caller can see no tab at all, render a graceful "no access" state
 * rather than redirect — there's nowhere to send them, and looping is the bug
 * this component exists to prevent.
 */
export function TabIndexRedirect({ tabs }: TabIndexRedirectProps) {
  const session = useAuthSession();
  const { search, hash } = useLocation();

  const firstVisible = tabs.find(
    (t) =>
      !t.disabled &&
      isTabAuthorized(
        t,
        session.features,
        session.permissions,
        session.is_supplier,
      ),
  );

  if (!firstVisible) {
    return (
      <EmptyState
        icon={Lock}
        title="No sections available"
        description="Your permissions don't grant access to any section of this record. Contact an administrator if you need access."
      />
    );
  }

  return <Navigate to={{ pathname: firstVisible.path, search, hash }} replace />;
}
